import React, { useContext } from "react";
import { useRouteMatch } from "react-router";
import { Badge, Box, Button, DataTable, IndexTable, Layout, Link, ProgressBar, Thumbnail } from "@shopify/polaris";
import { useSupplierShipmentDetailQuery } from "src/graphql/codegen_graphql";
import { PageLayout } from "src/components/shopify/PageLayout";
import { PolarisCard } from "src/components/shopify/PolarisCard";
import { PolarisText } from "src/components/shopify/PolarisrText";
import { PrinteSku4x3 } from "./print/PrinteSku4x3";
import { ContextUser } from "src/context/ContextUser";

export function InventoryShipementDetailScreen() {
  // const refPrint4x3 = useRef<HTMLButtonElement>(null);
  const { merchant } = useContext(ContextUser);
  let refPrint4x3: HTMLButtonElement;
  const router = useRouteMatch<{ id: string }>();
  const { data, loading } = useSupplierShipmentDetailQuery({
    variables: {
      shipmentId: Number(router.params.id),
    },
  });

  const progress =
    (Number(data?.supplierShipmentDetail?.shipment?.received || 0) /
      Number(data?.supplierShipmentDetail?.shipment?.qty || 0)) *
    100;

  const info = [
    [
      (<small>Shipment</small>) as any,
      (<small className="text-right">{data?.supplierShipmentDetail?.shipment?.shipmentCode}</small>) as any,
    ],
    [
      (<small>Total Units</small>) as any,
      (<small className="text-right">{data?.supplierShipmentDetail?.shipment?.qty}</small>) as any,
    ],
    [
      (<small>Total received</small>) as any,
      (<small className="text-right">{data?.supplierShipmentDetail?.shipment?.received}</small>) as any,
    ],
    [
      (<small>Warehouse</small>) as any,
      (
        <small className="text-right">
          <b>{data?.supplierShipmentDetail?.shipment?.arrivedAt?.name}</b>
        </small>
      ) as any,
    ],
  ];

  if (data) {
    for (const x of data?.supplierShipmentDetail?.shipment?.shipmentLog || []) {
      info.push([(<small>{x?.description}</small>) as any, (<small className="text-right">{x?.date}</small>) as any]);
    }
  }

  const barcode = data
    ? [
        {
          code: data.supplierShipmentDetail?.shipment?.shipmentCode,
          price: "",
          supplier: {
            name: merchant.name,
          },
          sku: [
            {
              sku_id: data.supplierShipmentDetail?.shipment?.shipmentCode,
              color: "",
              size: "",
              expiredDate: undefined,
            },
          ],
        },
      ]
    : [];

  return (
    <PageLayout pageProp={{ title: `Shipment Detail #${router.params.id}` }} loading={loading}>
      {!loading && data && (
        <Layout>
          <Layout.Section>
            {barcode.length === 1 && (
              <div style={{ position: "fixed", top: -9999 }}>
                <PrinteSku4x3 sku={barcode} refPrint={(ref: any) => (refPrint4x3 = ref)} />
              </div>
            )}
            <PolarisCard padding={"0"} roundedAbove="sm">
              <Box padding={"400"} borderColor="border" borderBlockEndWidth="025">
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <PolarisText as="h6" variant="headingSm">
                    Shipment Code: {data?.supplierShipmentDetail?.shipment?.shipmentCode || ""}
                  </PolarisText>
                  <Button size="slim" variant="primary" onClick={() => refPrint4x3.click()}>
                    Print Shipment Code
                  </Button>
                </div>
              </Box>
              <Box padding={"400"} borderColor="border" borderBlockEndWidth="025">
                <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "0.4rem" }}>
                  <PolarisText as="h6" variant="bodySm">
                    Progress unit received ({progress.toFixed(2) || 0}%)
                  </PolarisText>
                  <PolarisText as="h6" variant="bodySm">
                    {data?.supplierShipmentDetail?.shipment?.received || 0} /{" "}
                    {data?.supplierShipmentDetail?.shipment?.qty || 0}
                  </PolarisText>
                </div>
                <ProgressBar
                  animated
                  progress={progress}
                  tone={progress < 100 ? "critical" : progress > 100 ? "highlight" : "success"}
                />
              </Box>
              <Box>
                <IndexTable
                  headings={[{ title: "Units Shipment" }, { title: "Product" }, { title: "Stock" }, { title: "SKU" }]}
                  itemCount={data?.supplierShipmentDetail?.items?.length || 0}
                  selectable={false}
                >
                  {data.supplierShipmentDetail?.items?.map((item, index) => {
                    return (
                      <IndexTable.Row key={index} position={index} id={index + ""}>
                        <IndexTable.Cell>
                          <Badge
                            tone={
                              (item?.receivedQty || 0) < (item?.sentQty || 0)
                                ? "new"
                                : (item?.receivedQty || 0) > (item?.sentQty || 0)
                                ? "warning-strong"
                                : "success-strong"
                            }
                          >
                            {
                              (
                                <small>
                                  {item?.receivedQty || 0}/{item?.sentQty || 0}{" "}
                                  {(item?.receivedQty || 0) === (item?.sentQty || 0)
                                    ? "Complete"
                                    : (item?.receivedQty || 0) > (item?.sentQty || 0)
                                    ? "Exceed"
                                    : ""}
                                </small>
                              ) as any
                            }
                          </Badge>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <div style={{ display: "flex" }}>
                            <Thumbnail alt="" source={item?.sku?.product?.thumbnail || ""} size="medium" />
                            <div style={{ width: 150 }} className="ml-3">
                              <PolarisText as="strong" variant="bodySm" truncate>
                                {item?.sku?.product?.title}
                              </PolarisText>
                              <small>{item?.sku?.product?.supplierCode}</small>
                              <br />
                              <small>L192: {item?.sku?.product?.code}</small>
                            </div>
                          </div>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <small>Weight: {item?.sku?.product?.weight}g</small>
                          <br />
                          <small>Box Size: {item?.sku?.product?.boxSize}</small>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Link
                            url={`/applications/inventories/sku?id=${item?.sku?.product?.id}&sku_id=${item?.sku?.id}`}
                            external
                            target="_blank"
                            removeUnderline
                          >
                            <small>Color: {item?.sku?.option1}</small>
                            <br />
                            <small>Size: {item?.sku?.option2}</small>
                          </Link>
                        </IndexTable.Cell>
                      </IndexTable.Row>
                    );
                  })}
                </IndexTable>
              </Box>
            </PolarisCard>
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <PolarisCard padding={"0"} roundedAbove="sm">
              <Box padding={"400"} borderColor="border" borderBlockEndWidth="025">
                <PolarisText as="h6" variant="headingSm">
                  Shipment Info
                </PolarisText>
              </Box>
              <Box>
                <DataTable headings={[]} columnContentTypes={["text", "text"]} rows={info} />
              </Box>
            </PolarisCard>
          </Layout.Section>
        </Layout>
      )}
    </PageLayout>
  );
}
